import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOptions,
  selectArticleStatusOptions,
  selectArticleLabelOptions,
  selectContactStatusOptions,
  selectContactPriorityOptions,
  selectCourseLevelOptions,
  selectCourseStatusOptions,
  selectAdvertisementTypeOptions,
  selectAdvertisementSlotOptions,
  selectConfigLoading
} from '../store/slices/configSlice';

/**
 * Custom hook to fetch and use dynamic options
 * @param {string} optionType - Type of options to fetch (e.g., 'articleStatus', 'contactPriority')
 * @returns {Object} { options, loading, getOption, getLabel, getVariant }
 */
export const useOptions = (optionType) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectConfigLoading);
  const hasFetched = useRef(false);

  // Select appropriate options based on type
  const articleStatus = useSelector(selectArticleStatusOptions);
  const articleLabels = useSelector(selectArticleLabelOptions);
  const contactStatus = useSelector(selectContactStatusOptions);
  const contactPriority = useSelector(selectContactPriorityOptions);
  const courseLevel = useSelector(selectCourseLevelOptions);
  const courseStatus = useSelector(selectCourseStatusOptions);
  const advertisementTypes = useSelector(selectAdvertisementTypeOptions);
  const advertisementSlots = useSelector(selectAdvertisementSlotOptions);

  const optionsMap = {
    articleStatus,
    articleLabels,
    contactStatus,
    contactPriority,
    courseLevel,
    courseStatus,
    advertisementTypes,
    advertisementSlots
  };

  const options = optionsMap[optionType] || [];

  useEffect(() => {
    // Only fetch once if options are empty and not already loading
    if (options.length === 0 && !loading.options && !hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchOptions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  /**
   * Get option object by value
   * @param {any} value - Option value to find
   * @returns {Object|null} Option object or null
   */
  const getOption = (value) => {
    return options.find(opt => String(opt.value) === String(value)) || null;
  };

  /**
   * Get label for a value
   * @param {any} value - Option value
   * @returns {string} Label or value as string
   */
  const getLabel = (value) => {
    const option = getOption(value);
    return option?.label || String(value);
  };

  /**
   * Get variant for a value
   * @param {any} value - Option value
   * @returns {string} Variant or 'default'
   */
  const getVariant = (value) => {
    const option = getOption(value);
    return option?.variant || 'default';
  };

  /**
   * Get icon for a value
   * @param {any} value - Option value
   * @returns {string|null} Icon name or null
   */
  const getIcon = (value) => {
    const option = getOption(value);
    return option?.icon || null;
  };

  /**
   * Format options for Select component
   * @returns {Array} Array of {value, label} objects
   */
  const getSelectOptions = () => {
    return options.map(opt => ({
      value: opt.value,
      label: opt.label
    }));
  };

  return {
    options,
    loading: loading.options,
    getOption,
    getLabel,
    getVariant,
    getIcon,
    getSelectOptions
  };
};

/**
 * Hook to get all options at once
 * @returns {Object} All option types
 */
export const useAllOptions = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectConfigLoading);
  const hasFetched = useRef(false);

  const articleStatus = useSelector(selectArticleStatusOptions);
  const articleLabels = useSelector(selectArticleLabelOptions);
  const contactStatus = useSelector(selectContactStatusOptions);
  const contactPriority = useSelector(selectContactPriorityOptions);
  const courseLevel = useSelector(selectCourseLevelOptions);
  const courseStatus = useSelector(selectCourseStatusOptions);
  const advertisementTypes = useSelector(selectAdvertisementTypeOptions);
  const advertisementSlots = useSelector(selectAdvertisementSlotOptions);

  useEffect(() => {
    // Only fetch once if no options are loaded and not already loading
    const hasOptions = articleStatus.length > 0 || contactStatus.length > 0;
    if (!hasOptions && !loading.options && !hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchOptions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return {
    articleStatus,
    articleLabels,
    contactStatus,
    contactPriority,
    courseLevel,
    courseStatus,
    advertisementTypes,
    advertisementSlots,
    loading: loading.options
  };
};

export default useOptions;
